import { Helmet } from "react-helmet-async";

// Organization Schema - used on all pages
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Chathero",
  url: "https://chathero.ai",
  logo: "https://chathero.ai/assets/logo_chathero.png",
  description: "AI Agent Plattform für den Mittelstand – Marketing, Sales und Kundenservice automatisieren",
  address: {
    "@type": "PostalAddress",
    addressLocality: "München",
    addressCountry: "DE",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+49-151-72544815",
    contactType: "sales",
    availableLanguage: ["German", "English"],
  },
  sameAs: [
    "https://www.linkedin.com/company/chathero",
    "https://www.instagram.com/chathero.ai",
  ],
  foundingDate: "2021",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    value: 35,
  },
};

// Software Application Schema
export const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Chathero",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: "165",
    priceCurrency: "EUR",
    priceValidUntil: "2026-12-31",
    description: "Essential Plan - Ab €165/Monat",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "127",
  },
  featureList: [
    "AI Agents für Marketing, Sales, Service",
    "WhatsApp Business Integration",
    "Unified Inbox",
    "AI Knowledge Base",
    "HubSpot & CRM Integration",
    "DSGVO-konform & Hosting in Deutschland",
  ],
};

// WebPage Schema Generator
export const createWebPageSchema = (
  name: string,
  description: string,
  url: string,
  breadcrumbs?: { name: string; url: string }[]
) => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: `https://chathero.ai${url}`,
    inLanguage: "de-DE",
    isPartOf: {
      "@type": "WebSite",
      name: "Chathero",
      url: "https://chathero.ai",
    },
    publisher: organizationSchema,
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: `https://chathero.ai${item.url}`,
      })),
    };
  }

  return schema;
};

// FAQ Page Schema Generator
export const createFAQSchema = (
  faqs: { question: string; answer: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// Job Posting Schema Generator
export const createJobPostingSchema = (job: {
  title: string;
  description: string;
  location: string;
  type: string;
  publishDate: string;
  remote: boolean;
  responsibilities: string[];
  requirements: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: job.title,
  description: job.description,
  datePosted: job.publishDate,
  validThrough: new Date(
    new Date(job.publishDate).getTime() + 90 * 24 * 60 * 60 * 1000
  ).toISOString(),
  employmentType: job.type === "Vollzeit" ? "FULL_TIME" : "PART_TIME",
  hiringOrganization: {
    "@type": "Organization",
    name: "Chathero",
    sameAs: "https://chathero.ai",
    logo: "https://chathero.ai/assets/logo_chathero.png",
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressLocality: "München",
      addressCountry: "DE",
    },
  },
  jobLocationType: job.remote ? "TELECOMMUTE" : undefined,
  applicantLocationRequirements: job.remote
    ? {
        "@type": "Country",
        name: "Germany",
      }
    : undefined,
  responsibilities: job.responsibilities.join(". "),
  qualifications: job.requirements.join(". "),
  industry: "Software / SaaS",
  occupationalCategory: "Sales",
});

// Article/Blog Post Schema Generator
export const createArticleSchema = (article: {
  title: string;
  description: string;
  slug: string;
  publishDate: string;
  author: { name: string };
  category: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.description,
  url: `https://chathero.ai/blog/${article.slug}`,
  datePublished: article.publishDate,
  dateModified: article.publishDate,
  author: {
    "@type": "Person",
    name: article.author.name,
  },
  publisher: {
    "@type": "Organization",
    name: "Chathero",
    logo: {
      "@type": "ImageObject",
      url: "https://chathero.ai/assets/logo_chathero.png",
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://chathero.ai/blog/${article.slug}`,
  },
  articleSection: article.category,
  inLanguage: "de-DE",
});

// Product Schema for Product Pages
export const createProductSchema = (product: {
  name: string;
  description: string;
  url: string;
  features: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.description,
  url: `https://chathero.ai${product.url}`,
  brand: {
    "@type": "Brand",
    name: "Chathero",
  },
  offers: {
    "@type": "Offer",
    price: "165",
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    seller: organizationSchema,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "127",
  },
});

// Speakable Schema Generator (for voice search / AI assistants)
export const createSpeakableSchema = (
  url: string,
  cssSelectors: string[] = ["h1", "h2", ".hero-description", "main p"]
) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `https://chathero.ai${url}`,
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  },
});

/**
 * StructuredDataHead – renders JSON-LD script tags via Helmet for SSG compatibility.
 */
export const StructuredDataHead = ({ schemas }: { schemas: object[] }) => {
  if (!schemas || schemas.length === 0) return null;
  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          data-structured-data="true"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

/**
 * Legacy hook – still injects via DOM for backward compatibility.
 * Prefer <StructuredDataHead /> for SSG support.
 */
export const useStructuredData = (schemas: object[]) => {
  // No-op in SSR; for client-side, Helmet handles it via StructuredDataHead.
  // Kept for backward compatibility.
};

/**
 * LLMMetadataHead – renders LLM/GEO-specific meta tags via Helmet.
 */
export const LLMMetadataHead = ({
  title,
  description,
  keywords,
  author = "Chathero",
  canonicalUrl,
  ogImage = "https://chathero.ai/assets/logo_chathero.png",
  articlePublishedTime,
  articleAuthor,
  aiCategory = "SaaS, AI, Customer Service, Chatbot",
  aiEntityType = "AI Agent Platform",
  aiUseCase,
  aiIndustry,
  aiFeatures,
}: {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  canonicalUrl?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleAuthor?: string;
  aiCategory?: string;
  aiEntityType?: string;
  aiUseCase?: string;
  aiIndustry?: string;
  aiFeatures?: string;
}) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
    {keywords && <meta name="keywords" content={keywords} />}
    <meta name="author" content={author} />
    <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

    {/* Open Graph */}
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:type" content={articlePublishedTime ? "article" : "website"} />
    <meta property="og:image" content={ogImage} />
    <meta property="og:site_name" content="Chathero" />
    <meta property="og:locale" content="de_DE" />
    {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

    {/* Twitter */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={ogImage} />

    {/* Article specific */}
    {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
    {articleAuthor && <meta property="article:author" content={articleAuthor} />}

    {/* AI/LLM-specific metadata */}
    <meta name="ai:description" content={description} />
    <meta name="ai:category" content={aiCategory} />
    <meta name="ai:company" content="Chathero" />
    <meta name="ai:product_type" content={aiEntityType} />
    <meta name="ai:language" content="de" />
    <meta name="ai:geo" content="DE, DACH" />
    <meta name="ai:pricing" content="Ab €165/Monat, 14 Tage kostenlos testen" />
    <meta name="ai:compliance" content="DSGVO-konform, Hosting in Deutschland" />
    {aiUseCase && <meta name="ai:use_case" content={aiUseCase} />}
    {aiIndustry && <meta name="ai:industry" content={aiIndustry} />}
    {aiFeatures && <meta name="ai:features" content={aiFeatures} />}

    {/* Canonical */}
    {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
  </Helmet>
);

/**
 * Legacy hook – kept for backward compatibility.
 */
export const useLLMMetadata = (options: {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  canonicalUrl?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleAuthor?: string;
  aiCategory?: string;
  aiEntityType?: string;
  aiUseCase?: string;
  aiIndustry?: string;
  aiFeatures?: string;
}) => {
  // No-op: use <LLMMetadataHead /> instead.
};

export default useStructuredData;
